import { Injectable, Renderer2, RendererFactory2, ViewContainerRef, inject } from '@angular/core';

import { JoyrideScroll, JoyrideStep } from '../joyride.domain';
import { JoyrideDocumentService } from './joyride-document.service';

@Injectable({ providedIn: 'root' })
export class JoyrideBackdropService {
  private readonly documentService = inject(JoyrideDocumentService);
  private readonly rendererFactory = inject(RendererFactory2);

  private renderer: Renderer2;
  private currentBackdropContainer: unknown;
  private lastBackdropContainer: unknown;
  private backdropContent: unknown;
  private backdropTop: unknown;
  private backdropBottom: unknown;
  private backdropMiddleContainer: unknown;
  private backdropMiddleContent: unknown;
  private leftBackdrop: unknown;
  private targetBackdrop: unknown;
  private rightBackdrop: unknown;
  private elementRef: ViewContainerRef;
  private targetAbsoluteTop: number;
  private targetAbsoluteLeft: number;
  private lastXScroll: number = 0;
  private lastYScroll: number = 0;
  private disableTouchMove: () => void;

  constructor() {
    this.setRenderer();
  }

  draw(step: JoyrideStep): void {
    this.elementRef = step.targetViewContainer;
    this.targetAbsoluteTop = this.getTargetTotalTop(step);
    this.targetAbsoluteLeft = this.getTargetTotalLeft(step);

    this.currentBackdropContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.currentBackdropContainer, 'backdrop-container');
    this.renderer.setStyle(this.currentBackdropContainer, 'position', 'fixed');
    this.renderer.setStyle(this.currentBackdropContainer, 'top', '0px');
    this.renderer.setStyle(this.currentBackdropContainer, 'left', '0px');
    this.renderer.setStyle(this.currentBackdropContainer, 'width', '100%');
    this.renderer.setStyle(this.currentBackdropContainer, 'height', '100%');
    this.renderer.setStyle(this.currentBackdropContainer, 'z-index', '1000');
    this.renderer.setAttribute(this.currentBackdropContainer, 'id', 'td-wb-joyride-backdrop-' + step.name);
    this.disableTouchMove = this.renderer.listen(this.currentBackdropContainer, 'touchmove', (e: Event) => {
      e.preventDefault();
    });

    this.backdropContent = this.renderer.createElement('div');
    this.renderer.addClass(this.backdropContent, 'backdrop-content');
    this.renderer.setStyle(this.backdropContent, 'position', 'relative');
    this.renderer.setStyle(this.backdropContent, 'height', '100%');
    this.renderer.setStyle(this.backdropContent, 'display', 'flex');
    this.renderer.setStyle(this.backdropContent, 'flex-direction', 'column');
    this.renderer.appendChild(this.currentBackdropContainer, this.backdropContent);

    this.backdropTop = this.renderer.createElement('div');
    this.renderer.addClass(this.backdropTop, 'joyride-backdrop');
    this.renderer.addClass(this.backdropTop, 'backdrop-top');
    this.renderer.setStyle(this.backdropTop, 'width', '100%');
    this.renderer.setStyle(this.backdropTop, 'height', this.targetAbsoluteTop - this.lastYScroll + 'px');
    this.renderer.setStyle(this.backdropTop, 'flex-shrink', '0');
    this.renderer.setStyle(this.backdropTop, 'background-color', 'rgba(0, 0, 0, 0.32)');
    this.renderer.appendChild(this.backdropContent, this.backdropTop);

    this.backdropMiddleContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.backdropMiddleContainer, 'backdrop-middle-container');
    this.renderer.setStyle(this.backdropMiddleContainer, 'height', this.elementRef.element.nativeElement.offsetHeight + 'px');
    this.renderer.setStyle(this.backdropMiddleContainer, 'width', '100%');
    this.renderer.setStyle(this.backdropMiddleContainer, 'flex-shrink', '0');
    this.renderer.appendChild(this.backdropContent, this.backdropMiddleContainer);

    this.backdropMiddleContent = this.renderer.createElement('div');
    this.renderer.addClass(this.backdropMiddleContent, 'backdrop-middle-content');
    this.renderer.setStyle(this.backdropMiddleContent, 'display', 'flex');
    this.renderer.setStyle(this.backdropMiddleContent, 'width', '100%');
    this.renderer.setStyle(this.backdropMiddleContent, 'height', '100%');
    this.renderer.appendChild(this.backdropMiddleContainer, this.backdropMiddleContent);

    this.leftBackdrop = this.renderer.createElement('div');
    this.renderer.addClass(this.leftBackdrop, 'joyride-backdrop');
    this.renderer.addClass(this.leftBackdrop, 'backdrop-left');
    this.renderer.setStyle(this.leftBackdrop, 'flex-shrink', '0');
    this.renderer.setStyle(this.leftBackdrop, 'width', this.targetAbsoluteLeft - this.lastXScroll + 'px');
    this.renderer.setStyle(this.leftBackdrop, 'background-color', 'rgba(0, 0, 0, 0.32)');
    this.renderer.appendChild(this.backdropMiddleContent, this.leftBackdrop);

    this.targetBackdrop = this.renderer.createElement('div');
    this.renderer.addClass(this.targetBackdrop, 'backdrop-target');
    this.renderer.setStyle(this.targetBackdrop, 'flex-shrink', '0');
    this.renderer.setStyle(this.targetBackdrop, 'width', this.elementRef.element.nativeElement.offsetWidth + 'px');
    this.renderer.appendChild(this.backdropMiddleContent, this.targetBackdrop);

    this.rightBackdrop = this.renderer.createElement('div');
    this.renderer.addClass(this.rightBackdrop, 'joyride-backdrop');
    this.renderer.addClass(this.rightBackdrop, 'backdrop-right');
    this.renderer.setStyle(this.rightBackdrop, 'width', '100%');
    this.renderer.setStyle(this.rightBackdrop, 'background-color', 'rgba(0, 0, 0, 0.32)');
    this.renderer.appendChild(this.backdropMiddleContent, this.rightBackdrop);

    this.backdropBottom = this.renderer.createElement('div');
    this.renderer.addClass(this.backdropBottom, 'joyride-backdrop');
    this.renderer.addClass(this.backdropBottom, 'backdrop-bottom');
    this.renderer.setStyle(this.backdropBottom, 'width', '100%');
    this.renderer.setStyle(this.backdropBottom, 'height', '100%');
    this.renderer.setStyle(this.backdropBottom, 'background-color', 'rgba(0, 0, 0, 0.32)');
    this.renderer.appendChild(this.backdropContent, this.backdropBottom);

    this.removeLastBackdrop();
    this.drawCurrentBackdrop();
    this.lastBackdropContainer = this.currentBackdropContainer;
  }

  redraw(step: JoyrideStep, scroll: JoyrideScroll): void {
    if (this.lastYScroll !== scroll.scrollY) {
      this.lastYScroll = scroll.scrollY;
      if (this.elementRef) {
        this.handleVerticalScroll(step);
      }
    }
    if (this.lastXScroll !== scroll.scrollX) {
      this.lastXScroll = scroll.scrollX;
      if (this.elementRef) {
        this.handleHorizontalScroll(step);
      }
    }
  }

  remove(): void {
    this.removeLastBackdrop();
  }

  redrawTarget(step: JoyrideStep): void {
    this.targetAbsoluteLeft = this.getTargetTotalLeft(step);
    this.targetAbsoluteTop = this.getTargetTotalTop(step);
    this.handleVerticalScroll(step);
    this.handleHorizontalScroll(step);
  }

  private setRenderer(): void {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  private getTargetTotalTop(step: JoyrideStep): number {
    const targetVC = step.targetViewContainer;
    return step.isElementOrAncestorFixed
      ? this.documentService.getElementFixedTop(targetVC.element)
      : this.documentService.getElementAbsoluteTop(targetVC.element);
  }

  private getTargetTotalLeft(step: JoyrideStep): number {
    const targetVC = step.targetViewContainer;

    return step.isElementOrAncestorFixed
      ? this.documentService.getElementFixedLeft(targetVC.element)
      : this.documentService.getElementAbsoluteLeft(targetVC.element);
  }

  private handleHorizontalScroll(step: JoyrideStep): void {
    const newBackdropLeftWidth = step.isElementOrAncestorFixed ? this.targetAbsoluteLeft : this.targetAbsoluteLeft - this.lastXScroll;

    if (newBackdropLeftWidth >= 0) {
      this.renderer.setStyle(this.leftBackdrop, 'width', newBackdropLeftWidth + 'px');
      this.renderer.setStyle(this.targetBackdrop, 'width', this.elementRef.element.nativeElement.offsetWidth + 'px');
    } else {
      this.handleTargetPartialWidth(newBackdropLeftWidth);
    }
  }

  private handleTargetPartialWidth(newBackdropLeftWidth: number): void {
    this.renderer.setStyle(this.leftBackdrop, 'width', 0 + 'px');
    const visibleTargetWidth = this.elementRef.element.nativeElement.offsetWidth + newBackdropLeftWidth;
    if (visibleTargetWidth >= 0) {
      this.renderer.setStyle(this.targetBackdrop, 'width', visibleTargetWidth + 'px');
    } else {
      this.renderer.setStyle(this.targetBackdrop, 'width', 0 + 'px');
    }
  }

  private handleVerticalScroll(step: JoyrideStep): void {
    const newBackdropTopHeight = step.isElementOrAncestorFixed ? this.targetAbsoluteTop : this.targetAbsoluteTop - this.lastYScroll;

    if (newBackdropTopHeight >= 0) {
      this.renderer.setStyle(this.backdropTop, 'height', newBackdropTopHeight + 'px');
      this.renderer.setStyle(this.backdropMiddleContainer, 'height', this.elementRef.element.nativeElement.offsetHeight + 'px');
    } else {
      this.handleTargetPartialHeight(newBackdropTopHeight);
    }
  }

  private handleTargetPartialHeight(newBackdropTopHeight: number): void {
    this.renderer.setStyle(this.backdropTop, 'height', 0 + 'px');
    const visibleTargetHeight = this.elementRef.element.nativeElement.offsetHeight + newBackdropTopHeight;
    if (visibleTargetHeight >= 0) {
      this.renderer.setStyle(this.backdropMiddleContainer, 'height', visibleTargetHeight + 'px');
    } else {
      this.renderer.setStyle(this.backdropMiddleContainer, 'height', 0 + 'px');
    }
  }

  private removeLastBackdrop(): void {
    if (this.lastBackdropContainer) {
      if (this.disableTouchMove) {
        this.disableTouchMove();
      }
      this.renderer.removeChild(document.body, this.lastBackdropContainer);
      this.lastBackdropContainer = undefined;
    }
  }

  private drawCurrentBackdrop(): void {
    this.renderer.appendChild(document.body, this.currentBackdropContainer);
  }
}
